import React, { useMemo } from 'react';
import OrderSummaryCard from '../../OrderSummaryCard';
import { CHECKOUT_STEPS, type DesignViewCommonProps, DesignViewEditSectionEnum } from '../common/design-common';
import type { FormData, PaymentInformationData, ShippingAddressData, UserIdentificationData } from '../../../types';
import type { GiftProgressState } from '../../../offer.types';
import { getMethodPrice, toDiscountBreakdown, toInstallmentInfo } from '../../../utils/pricing';
import UserIdentificationForm from '../../forms/UserIdentificationForm';
import ShippingAddressForm from '../../forms/ShippingAddressForm';
import PaymentForm from '../../forms/PaymentForm';
import IdentificationView from './components/IdentificationView';
import ShippingAddressView from './components/ShippingAddressView';
import PaymentView from './components/PaymentView';
import DesignDefaultSectionBlock from './components/DesignDefaultSectionBlock';

interface DesignDefaultProps extends DesignViewCommonProps {
  editingSection: DesignViewEditSectionEnum;
  onSaveSection: (updatedData: Partial<FormData>) => void;
  onPlaceOrder: () => void;
  onChangePaymentMethod?: (data: PaymentInformationData) => void;
  onCardElementReady?: (handlers: { getCardElement: () => any; getStripe: () => unknown }) => void;
  /** Recebe o token reutilizável `pgpm_` (Pagou) quando há upsell one-click. */
  onReusableToken?: (token: string) => void;
  /** Sinaliza ao Payment Element da Pagou que deve gerar token reutilizável (`mode: "upsell"`). */
  hasOneClickUpsell?: boolean;
  onOrderBumpsChange?: (nextAcceptedNanoIds: string[]) => void;
  giftStates?: GiftProgressState[];
  onApplyGiftCoupon?: (couponCode: string) => Promise<void>;
  /** Blur de email/telefone válido na identificação — alimenta o cart antes do submit. */
  onContactCaptured?: (data: UserIdentificationData) => void;
}

interface SectionConfig {
  id: DesignViewEditSectionEnum;
  stepNumber: number;
  title: string;
  formComponent: React.ComponentType<any>;
  viewComponent: React.ComponentType<any>;
  getInitialData: (formData: FormData) => any;
  getSaveData: (data: any) => Partial<FormData>;
}

const DesignDefault: React.FC<DesignDefaultProps> = ({
  formData,
  shopifyOffer,
  editingSection,
  onEdit,
  onCouponApply,
  onSaveSection,
  onPlaceOrder,
  onChangePaymentMethod,
  onCardElementReady,
  onReusableToken,
  hasOneClickUpsell,
  onOrderBumpsChange,
  giftStates,
  onApplyGiftCoupon,
  onContactCaptured,
  checkoutConfig,
}) => {
  const { userIdentification, shippingAddress, paymentInformation, orderSummary } = formData;

  // Memoiza os status de conclusão para evitar recálculos
  const sectionStatus = useMemo(() => {
    const status = {
      [DesignViewEditSectionEnum.IDENTIFICATION]: CHECKOUT_STEPS.find(step => step.id === DesignViewEditSectionEnum.IDENTIFICATION)?.isCompleted(formData) ?? false,
      [DesignViewEditSectionEnum.SHIPPING]: CHECKOUT_STEPS.find(step => step.id === DesignViewEditSectionEnum.SHIPPING)?.isCompleted(formData) ?? false,
      [DesignViewEditSectionEnum.PAYMENT]: CHECKOUT_STEPS.find(step => step.id === DesignViewEditSectionEnum.PAYMENT)?.isCompleted(formData) ?? false,
    };
    
    
    return status;
  }, [formData]);

  // Configuração centralizada das seções
  const sectionsConfig: SectionConfig[] = [
    {
      id: DesignViewEditSectionEnum.IDENTIFICATION,
      stepNumber: 1,
      title: "Identificação",
      formComponent: UserIdentificationForm,
      viewComponent: IdentificationView,
      getInitialData: (formData: FormData) => {
        const idSteps = formData.shopifyOffer.checkoutTheme?.checkout?.config?.steps?.identification;
        return {
          ...formData.userIdentification,
          cpfOnIdentification: typeof idSteps?.requireCpfOnlyAtPayment === 'boolean'
            ? !idSteps.requireCpfOnlyAtPayment
            : Boolean(formData.shopifyOffer.store?.cpfOnIdentification),
          requestBirthDate: Boolean(idSteps?.requestBirthDate),
        };
      },
      getSaveData: (data: UserIdentificationData) => ({ userIdentification: data }),
    },
    {
      id: DesignViewEditSectionEnum.SHIPPING,
      stepNumber: 2,
      title: "Entrega",
      formComponent: ShippingAddressForm,
      viewComponent: ShippingAddressView,
      getInitialData: (formData: FormData) => ({
        ...formData.shippingAddress,
      }),
      getSaveData: (data: ShippingAddressData) => ({ shippingAddress: data }),
    },
    {
      id: DesignViewEditSectionEnum.PAYMENT,
      stepNumber: 3,
      title: "Pagamento",
      formComponent: PaymentForm,
      viewComponent: PaymentView,
      getInitialData: (formData: FormData) => {
        // CPF do cartão: sempre em branco ao abrir (não preencher com CPF da identificação)
        const availableCpfPixBoleto = [
          formData.userIdentification.cpf,
          formData.paymentInformation.pix?.cpf,
          formData.paymentInformation.boleto?.cpf,
        ].find(c => c && c.trim().length > 0) ?? '';

        const getCpfValuePixBoleto = (value: string | undefined) =>
          (value ?? '').trim().length > 0 ? value : availableCpfPixBoleto;

        return {
          ...formData.paymentInformation,
          creditCard: {
            ...formData.paymentInformation.creditCard,
            cpf: formData.paymentInformation.creditCard?.cpf ?? '',
          },
          pix: {
            ...formData.paymentInformation.pix,
            cpf: getCpfValuePixBoleto(formData.paymentInformation.pix?.cpf),
          },
          boleto: {
            ...formData.paymentInformation.boleto,
            cpf: getCpfValuePixBoleto(formData.paymentInformation.boleto?.cpf),
          },
        };
      },
      getSaveData: (data: PaymentInformationData) => ({ paymentInformation: data }),
    },
  ];

  const renderSection = (config: SectionConfig) => {
    const isCompleted = sectionStatus[config.id];
    const isCurrentStep = editingSection === config.id;

    if (isCurrentStep) {
      const FormComponent = config.formComponent;
      const initialData = config.getInitialData(formData);

      return (
        <div key={config.id} data-step-section={config.id}>
          <FormComponent
            {...(config.id === DesignViewEditSectionEnum.PAYMENT ? {
              paymentData: initialData,
              availablePaymentMethods: shopifyOffer?.payment?.methods,
              wallets: shopifyOffer?.payment?.wallets,
              creditCardPrimaryGateway: shopifyOffer?.payment?.creditCardPrimaryGateway,
              creditCardGatewayRequires3DS: shopifyOffer?.payment?.creditCardGatewayRequires3DS,
              stripePublishableKey: shopifyOffer?.payment?.stripePublishableKey,
              pagouPublicKey: shopifyOffer?.payment?.pagouPublicKey,
              pagouProduction: shopifyOffer?.payment?.pagouProduction,
              pagarmePublicKey: shopifyOffer?.payment?.pagarmePublicKey,
              paymentMethodDiscounts: shopifyOffer?.store?.paymentMethodDiscounts,
              paymentMethodPrices: shopifyOffer?.pricing ? {
                PIX: getMethodPrice(shopifyOffer, 'pix'),
                CARD: getMethodPrice(shopifyOffer, 'card'),
                BOLETO: getMethodPrice(shopifyOffer, 'billet'),
                APPLE_PAY: getMethodPrice(shopifyOffer, 'apple_pay'),
                GOOGLE_PAY: getMethodPrice(shopifyOffer, 'google_pay'),
              } : undefined,
              installmentOptions: shopifyOffer?.pricing?.byMethod?.card?.installments?.available_installments,
              onCardElementReady: config.id === DesignViewEditSectionEnum.PAYMENT ? onCardElementReady : undefined,
              onReusableToken: config.id === DesignViewEditSectionEnum.PAYMENT ? onReusableToken : undefined,
              hasOneClickUpsell: config.id === DesignViewEditSectionEnum.PAYMENT ? hasOneClickUpsell : undefined,
              orderBumps: shopifyOffer?.order_bumps,
              acceptedOrderBumpNanoIds: (shopifyOffer?.items ?? []).filter(i => i.source === 'order_bump').map(i => i.nanoId).filter((id): id is string => !!id),
              onOrderBumpsChange: config.id === DesignViewEditSectionEnum.PAYMENT ? onOrderBumpsChange : undefined,
              preferredPaymentMethod: checkoutConfig?.steps?.payment?.preferredPaymentMethod,
            } : { initialData })}
            {...(config.id === DesignViewEditSectionEnum.IDENTIFICATION ? { onContactCaptured } : {})}
            onSave={(data: any) => onSaveSection(config.getSaveData(data))}
            onPlaceOrder={config.id === DesignViewEditSectionEnum.PAYMENT ? onPlaceOrder : undefined}
            onChangePaymentMethod={config.id === DesignViewEditSectionEnum.PAYMENT ? onChangePaymentMethod : undefined}
          />
        </div>
      );
    }

    const ViewComponent = config.viewComponent;
    const viewData = config.id === DesignViewEditSectionEnum.IDENTIFICATION ? userIdentification :
                    config.id === DesignViewEditSectionEnum.SHIPPING ? shippingAddress :
                    paymentInformation;

    return (
      <div key={config.id} data-step-section={config.id}>
        <DesignDefaultSectionBlock
          stepNumber={config.stepNumber}
          title={config.title}
          isCompleted={isCompleted}
          onEdit={() => onEdit(config.id)}
        >
          <ViewComponent
            {...(config.id === DesignViewEditSectionEnum.IDENTIFICATION ? { userIdentification: viewData, isCompleted } :
                 config.id === DesignViewEditSectionEnum.SHIPPING ? { shippingAddress: viewData, isCompleted } :
                 { paymentInformation: viewData })}
          />
        </DesignDefaultSectionBlock>
      </div>
    );
  };

  return (
    <div className="container mx-auto lg:p-6 bg-pink-50/30 dark:bg-pink-900/20 min-h-[calc(100vh-200px)]">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Column 1: Identification & Delivery */}
        <div className="lg:col-span-1 space-y-5 lg:max-w-lg">
          {renderSection(sectionsConfig[0])}
          {renderSection(sectionsConfig[1])}
        </div>

        {/* Column 2: Payment */}
        <div className="lg:col-span-1 space-y-5">
          {renderSection(sectionsConfig[2])}
        </div>

        {/* Column 3: Order Summary */}
        <div className="lg:col-span-1 space-y-5">
          <div>

            <OrderSummaryCard
              summary={orderSummary}
              onApplyCoupon={onCouponApply}
              discountBreakdown={toDiscountBreakdown(shopifyOffer)}
              installmentInfo={toInstallmentInfo(shopifyOffer)}
              orderBumps={shopifyOffer?.order_bumps}
              acceptedOrderBumpNanoIds={(shopifyOffer?.items ?? []).filter(i => i.source === 'order_bump').map(i => i.nanoId).filter((id): id is string => !!id)}
              onOrderBumpsChange={onOrderBumpsChange}
              giftStates={giftStates}
              onApplyGiftCoupon={onApplyGiftCoupon}
              config={checkoutConfig?.summary}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignDefault;
export { default as Stepper } from './Stepper'; 